import { useCallback, useState } from 'react';
import CustomDataGridResult from '../../../utils/Datagrid/CustomForecastDataGridResult';
import {
  CustomEditFixedCell,
  CustomForecastCell,
  CustomForecastHeader,
  RenderEditForecastCell,
  culturesSelectValues,
  customFixedCell,
} from '../../../utils/Datagrid/CustomForecastDataGridUtils';
import {
  CustomSortableHeader,
  childrenTotalSurface,
  getFamily,
  totalSurfaceComparator,
} from 'utils/Datagrid/CustomGenericDataGridUtils';
import {
  filterParcelFromActivityDate,
  getCultureCampaignKey,
  getCultureCampaignName,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { useTranslation } from 'react-i18next';

const ParcelsForecast = ({
  exploitationContext,
  dataGridLoading,
  setIsAllowedSubmit,
  stackedRequestsRef,
}) => {
  const { t } = useTranslation();
  const [sortModel, setSortModel] = useState([
    {
      field: 'parcel',
      sort: 'asc',
    },
  ]);
  const forecastCampaign = exploitationContext?.forecastCampaign;
  const renderEditForecastCell = (params) => {
    return <RenderEditForecastCell exploitationContext={exploitationContext} {...params} />;
  };
  const renderEditFixedCell = (params) => {
    return <CustomEditFixedCell {...params} />;
  };

  const columns = [
    {
      field: 'parcel',
      headerName: t('common.parcel'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1.2,
      sortable: false,
      cellClassName: (params) =>
        params.cellMode === 'view' && params?.row?.parentId?.length > 1 && 'first-children',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'parcel'}
          name={t('common.parcel')}
        />
      ),
    },
    {
      field: 'surface',
      headerName: t('common.surfaceHa'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 0.6,
      sortable: false,
      valueGetter: (value, row) => ({
        totalSurface: row.totalSurface,
        surface: row.surface,
      }),
      valueFormatter: (value) => {
        if (value.totalSurface !== value.surface) {
          return `${value.surface} (Total: ${value.totalSurface
            .toFixed(2)
            .replace(/[.,]00$/, '')})`;
        }
        return value.surface;
      },
      sortComparator: totalSurfaceComparator,
      cellClassName: (params) => params?.row?.parentId?.length > 1 && 'parcel_children',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'surface'}
          name={t('common.surfaceHa')}
        />
      ),
    },
    {
      field: 'isIrrigable',
      headerName: t('common.irrigability'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 0.4,
      sortable: false,
      cellClassName: (params) => params?.row?.parentId?.length > 1 && 'parcel_children',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'isIrrigable'}
          name={t('common.irrigability')}
        />
      ),
    },
    {
      field: 'groundType',
      headerName: t('column.soilTypes'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      cellClassName: (params) => params?.row?.parentId?.length > 1 && 'parcel_children',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'groundType'}
          name={t('column.soilTypes')}
        />
      ),
    },
    {
      field: 'previousCulture2',
      headerName: `${forecastCampaign?.startYear - 2}-${forecastCampaign?.endYear - 2}`,
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      cellClassName: (params) => params?.row?.parentId?.length > 1 && 'parcel_children',
      renderCell: (params) => {
        const value = params.value;
        const displayValue = value && value.startsWith('message.') ? t(value) : value;
        return <div>{displayValue}</div>;
      },
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'previousCulture2'}
          name={`${forecastCampaign?.startYear - 2}-${forecastCampaign?.endYear - 2}`}
        />
      ),
    },
    {
      field: 'previousCulture1',
      headerName: `${forecastCampaign?.startYear - 1}-${forecastCampaign?.endYear - 1}`,
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      cellClassName: (params) => params?.row?.parentId?.length > 1 && 'parcel_children',
      renderCell: (params) => {
        const value = params.value;
        const displayValue = value && value.startsWith('message.') ? t(value) : value;
        return <div>{displayValue}</div>;
      },
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'previousCulture1'}
          name={`${forecastCampaign.startYear - 1}-${forecastCampaign.endYear - 1}`}
        />
      ),
    },
    {
      field: 'forecast',
      headerName: `${forecastCampaign?.startYear}-${forecastCampaign?.endYear} (${t('label.forecast')})`,
      type: 'singleSelect',
      renderHeader: CustomForecastHeader,
      sortable: false,
      headerClassName: 'forecast-header header-material',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      editable: true,
      valueOptions: (params) => {
        return culturesSelectValues(params.row.forecast, exploitationContext, t);
      },
      renderEditCell: renderEditForecastCell,
      renderCell: (params) => CustomForecastCell(params, stackedRequestsRef, t),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'forecast'}
          name={CustomForecastHeader(forecastCampaign.startYear, t('label.forecast'))}
        />
      ),
    },
    {
      field: 'isFixed',
      headerName: t('label.forecastFixed'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      editable: true,
      renderEditCell: renderEditFixedCell,
      renderCell: (params) => customFixedCell(params, stackedRequestsRef),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'isFixed'}
          name={t('label.forecastFixed')}
        />
      ),
    },
  ];

  const processedRows = filterParcelFromActivityDate(
    exploitationContext?.parcels,
    forecastCampaign,
  )?.map((parcel) => {
    return {
      id: parcel?.id,
      parcel: parcel?.name,
      surface: parcel?.surface,
      totalSurface: parcel?.isParent
        ? childrenTotalSurface(parcel?.id, exploitationContext?.parcels) + parcel?.surface
        : parcel?.surface,
      parcelId: parcel?.id,
      isIrrigable: parcel?.isIrrigable.toString() === 'true' ? t('common.yes') : t('common.no'),
      groundType: parcel?.groundType?.name,
      previousCulture2: getCultureCampaignName(parcel.parcelCultures, 2, forecastCampaign),
      previousCulture1: getCultureCampaignName(parcel.parcelCultures, 1, forecastCampaign),
      forecast: getCultureCampaignName(parcel.parcelCultures, 0, forecastCampaign, 'forecast'),
      isFixed: parcel?.parcelCultures[getCultureCampaignKey(0, forecastCampaign)]?.isFixed ?? false,
      parentId: parcel?.parent ? getFamily(parcel, exploitationContext?.parcels) : [parcel.id],
      isParent: parcel?.isParent ?? false,
    };
  });

  const processRowUpdate = useCallback(
    async (updatedRow, previousRow) => {
      const updatedForecastCultureId = exploitationContext?.cultures?.find(
        (culture) => culture.name === updatedRow.forecast,
      )?.id;

      const isFixedSameAsOriginal = previousRow.isFixed === updatedRow.isFixed;
      const isForecastSameAsOriginal = previousRow.forecast === updatedRow.forecast;

      let existInStackedRequest = false;

      stackedRequestsRef.current.forEach((request) => {
        if (request?.rowId === updatedRow.id) {
          existInStackedRequest = true;
        }
      });

      if (previousRow.isFixed !== updatedRow.isFixed) {
        const data = {
          parcel: updatedRow.parcelId,
          culture: updatedForecastCultureId,
          isFixed: updatedRow.isFixed,
          updatedFixed: true,
        };

        if (!existInStackedRequest) {
          stackedRequestsRef.current.push({
            ...data,
            rowId: updatedRow.id,
          });
        } else if (!isFixedSameAsOriginal) {
          stackedRequestsRef.current = stackedRequestsRef.current.map((request) => {
            if (request?.rowId === updatedRow.id) {
              return {
                ...request,
                ...data,
              };
            } else {
              return request;
            }
          });
        } else if (isFixedSameAsOriginal && !isForecastSameAsOriginal) {
          stackedRequestsRef.current = stackedRequestsRef.current.map((request) => {
            if (request?.rowId === updatedRow.id) {
              delete request?.updatedFixed;
              delete data.updatedFixed;
              return {
                ...request,
                ...data,
              };
            } else {
              return request;
            }
          });
        } else {
          stackedRequestsRef.current = stackedRequestsRef.current.filter(
            (request) => request?.rowId !== updatedRow.id,
          );
        }
        setIsAllowedSubmit(stackedRequestsRef.current.length ? true : false);

        return updatedRow;
      }

      if (
        previousRow.forecast !== updatedRow.forecast &&
        updatedRow.forecast !== t('message.noForecast')
      ) {
        const data = {
          parcel: updatedRow.parcelId,
          culture: updatedForecastCultureId,
          isFixed: updatedRow.isFixed,
          forecast: true,
        };

        if (!existInStackedRequest) {
          stackedRequestsRef.current.push({
            ...data,
            rowId: updatedRow.id,
          });
        } else if (!isForecastSameAsOriginal) {
          stackedRequestsRef.current = stackedRequestsRef.current.map((request) => {
            if (request?.rowId === updatedRow.id) {
              return {
                ...request,
                ...data,
              };
            } else {
              return request;
            }
          });
        } else if (isForecastSameAsOriginal && !isFixedSameAsOriginal) {
          stackedRequestsRef.current = stackedRequestsRef.current.map((request) => {
            if (request?.rowId === updatedRow.id) {
              return {
                ...request,
                ...data,
                forecast: false,
              };
            } else {
              return request;
            }
          });
        } else {
          stackedRequestsRef.current = stackedRequestsRef.current.filter(
            (request) => request?.rowId !== updatedRow.id,
          );
        }
        setIsAllowedSubmit(stackedRequestsRef.current.length ? true : false);

        return updatedRow;
      }

      return previousRow;
    },
    [exploitationContext],
  );

  const groupingColDef = {
    width: 5,
    valueFormatter: () => '',
    headerName: '',
    field: 'group',
    align: 'right',
    hideDescendantCount: true,
  };

  const getTreeDataPath = (row) => row.parentId;

  const getRowClassName = (params) => {
    console.log(params?.row);
    if (params?.row?.parentId && params?.row?.parentId?.length > 1) {
      return 'MuiDataGrid-ParcelChild--row';
    }
  };

  const isCellEditable = (params) => {
    if (params?.row?.parentId?.length > 1) {
      return false;
    }
    return params.field === 'isFixed' && params.row?.forecast === t('message.noForecast')
      ? false
      : true;
  };

  return (
    processedRows && (
      <div className="parcel-forecast-datagrid">
        <CustomDataGridResult
          rows={processedRows}
          columns={columns}
          isLoading={dataGridLoading}
          processRowUpdate={processRowUpdate}
          fullSize={true}
          sortModel={sortModel}
          setSortModel={setSortModel}
          groupingColDef={groupingColDef}
          treeData={true}
          getTreeDataPath={getTreeDataPath}
          getRowClassName={getRowClassName}
          isCellEditable={isCellEditable}
          fromRotateEffects
        />
      </div>
    )
  );
};

export default ParcelsForecast;
